import { defineStore } from 'pinia'
import { apiService } from '@/services/api.js'
import echoService from '@/services/echo.js'
import { useAuthStore } from '@/stores/auth.js'
import { useTranslationsStore } from '@/stores/translations.js'
import { MATCH_TYPES, ANNOUNCER_ENABLED_MATCH_TYPES, matchTypeName } from '@/constants/matchTypes.js'
import { DART_TYPE_MULTIPLIERS, DART_TYPE_FIXED_VALUES } from '@/constants/dartTypes.js'
import { ANALYTICS_EVENTS, HIGHLIGHT_SCORE_THRESHOLD } from '@/constants/analyticsEvents.ts'
import analytics from '@/services/analytics.ts'
import * as Sentry from '@sentry/vue'

const DARTBOT_PROFILE_IDS = new Set([11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24])
const RT_UPDATE_QUEUE_MAX_SIZE = 20
const RT_FLUSH_INTERVAL_MS = 1000
const BOT_REPLAY_LOG_PREFIX = '[BOT-REPLAY]'

/**
 * Resolve the gatekeeper entry for the player who is actually throwing.
 *
 * In team play the backend flags every member of the active team as
 * `is_turn`, so the first flagged entry is not necessarily the active
 * thrower. When multiple entries are flagged, disambiguate using the
 * `profile_id` on the current (last) turn of the current leg, which is
 * authoritative. Falls back to the first flagged entry.
 */
function resolveCurrentTurnEntry(gatekeeper, matchDetailData) {
    const turnEntries = (gatekeeper || []).filter(
        (entry) => entry?.is_turn === true || Number(entry?.is_turn) === 1,
    )
    if (!turnEntries.length) return null
    if (turnEntries.length === 1) return turnEntries[0]

    const sets = matchDetailData?.sets || []
    const currentSet = sets[sets.length - 1]
    const currentLeg = currentSet?.legs?.[currentSet.legs.length - 1]
    const turns = currentLeg?.turns || []
    const activeProfileId = turns[turns.length - 1]?.profile_id
    const matchedEntry = turnEntries.find((entry) => entry.profile_id === activeProfileId)

    return matchedEntry || turnEntries[0]
}

export const useMatchStore = defineStore('match', {
    state: () => ({
        setup: {
            dart_setup_id: null,
            id: null,
            matchmaking: false,
            online: false,
            matchtype_id: null,
            max_players: 10,
            min_players: 2,
            name: 'Game',
            profiles: [],
            settings: {},
            starting_player: null,
            currentStep: 1,
            totalSteps: 4,
            include_in_stats: true,
            creator_profile_id: null,
        },
        match: {
            id: null,
            matchtype_id: null,
            online: false,
            gatekeeper: [],
            matchData: {
                match_detail_data: {},
            },
            creator_profile_id: null,
        },
        activeChannel: null,
        activeChannelName: null,
        isJoiningChannel: false,
        editingTurn: null, // { set_index, leg_index, turn_index, competitor_turn_index, thrown_score, darts, competitorId }
        playMode: 'singles', // 'singles' or 'teams'
        showValidationError: false,
        team1UUID: null,
        team2UUID: null,
        isCreatingMatch: false,
        // When /start succeeded, so match_finished / match_abandoned can report how long the
        // match actually lasted. Null for a match restored from the API (we never saw it begin),
        // in which case the duration is simply left off the event.
        matchStartedAt: null,
        websocketHealth: {
            isDisconnected: false,
            pollingInterval: null,
            connectionHandlersBound: false,
        },
        isRealtimePaused: false,
        rtUpdateQueue: [],
        rtFlushTimer: null,
        isFlushingQueue: false,
        pendingRtSnapshot: null,
        botThrowIndicator: {
            visible: false,
            step: 0,
            teamId: null,
            turnKey: null,
            lastSnapshotKey: null,
        },
        matchEndedRemotely: null,
    }),

    persist: {
        omit: ['activeChannel', 'activeChannelName', 'editingTurn'],
    },

    getters: {
        getCurrentPlayer: (state) => {
            const currentTurnEntry = resolveCurrentTurnEntry(
                state.match.gatekeeper,
                state.match.matchData?.match_detail_data,
            )

            if (!currentTurnEntry) {
                return null
            }

            const profiles = state.match.matchData?.match_detail_data?.profiles || []
            const currentPlayer = profiles.find(
                (profile) => profile.id === currentTurnEntry.profile_id,
            )

            if (!currentPlayer) {
                console.warn(
                    'Current turn player not found in profiles:',
                    currentTurnEntry.profile_id,
                )
                return null
            }

            
            const colors = ['#0EC1FF', '#FF0000', '#C000FF', '#1AD200']
            const colorIndex = (currentPlayer.id || 0) % colors.length

            return {
                ...currentPlayer,
                color: currentPlayer.color || colors[colorIndex],
            }
        },
        getProfiles: (state) => {
            const profiles =
                state.match.matchData?.match_detail_data?.profiles || state.setup.profiles || []
            // Normalize: match_detail_data profiles use `id`, setup profiles use `profile_id`.
            // Ensure both fields are always present so callers can rely on either.
            return profiles.map((p) => ({
                ...p,
                profile_id: p.profile_id ?? p.id,
                id: p.id ?? p.profile_id,
            }))
        },
        getSettings: (state) => {
            return state.match.matchData?.match_detail_data?.settings || state.setup.settings || {}
        },
        getSets: (state) => {
            return state.match.matchData?.match_detail_data?.sets || []
        },
        getMatchData: (state) => {
            return state.match.matchData?.match_detail_data || {}
        },
        getCurrentSet() {
            const sets = this.getSets
            return sets[sets.length - 1] || null
        },
        getCurrentLeg() {
            const currentSet = this.getCurrentSet
            if (!currentSet?.legs?.length) return null
            return currentSet.legs[currentSet.legs.length - 1]
        },
        getCompetitorRemainingScore() {
            return (competitorId) => {
                try {
                    const currentLeg = this.getCurrentLeg
                    if (!currentLeg?.turns?.length) {
                        return this.getStartingScore
                    }

                    const playerTurns = currentLeg.turns.filter(
                        (turn) => turn.team === competitorId,
                    )

                    if (playerTurns.length > 0) {
                        const lastPlayerTurn = playerTurns[playerTurns.length - 1]
                        return lastPlayerTurn.remaining_score
                    }

                    return this.getStartingScore
                } catch (error) {
                    console.warn('Error getting competitor remaining score:', error)
                    return this.getStartingScore
                }
            }
        },

        getCompetitorLastThrows() {
            return (competitorId) => {
                try {
                    const currentLeg = this.getCurrentLeg
                    if (!currentLeg?.turns?.length) return []

                    const throws = this.getCompetitorThrowsFromLeg(currentLeg, competitorId)
                    console.log(
                        `getCompetitorLastThrows for ${competitorId}: found ${throws.length} completed`,
                    )

                    return throws
                } catch (error) {
                    console.warn('Error getting competitor last throws:', error)
                    return []
                }
            }
        },
        getCompetitorAverage() {
            return (competitorId) => {
                try {
                    const matchTypeId = this.match?.matchtype_id
                    const sets = this.getSets
                    if (!sets.length) return null

                    if (matchTypeId === MATCH_TYPES.CRICKET) {
                        let allThrows = []

                        for (const set of sets) {
                            if (set.legs && set.legs.length > 0) {
                                for (const leg of set.legs) {
                                    if (leg.turns && leg.turns.length > 0) {
                                        const legThrows = this.getCompetitorThrowsFromLeg(
                                            leg,
                                            competitorId,
                                        )
                                        allThrows.push(...legThrows)
                                    }
                                }
                            }
                        }

                        if (!allThrows.length) return null

                        const totalMarks = allThrows.reduce(
                            (sum, throwData) => sum + throwData.score,
                            0,
                        )
                        const totalTurns = allThrows.length

                        console.log(
                            `Cricket average: ${totalMarks} marks ÷ ${totalTurns} turns = ${totalTurns > 0 ? totalMarks / totalTurns : 0}`,
                        )

                        if (totalTurns > 0) {
                            const average = totalMarks / totalTurns
                            return Math.round(average * 100) / 100
                        }

                        return null
                    } else {
                        let allThrows = []

                        for (const set of sets) {
                            if (set.legs && set.legs.length > 0) {
                                for (const leg of set.legs) {
                                    if (leg.turns && leg.turns.length > 0) {
                                        const legThrows = this.getCompetitorThrowsFromLeg(
                                            leg,
                                            competitorId,
                                        )
                                        allThrows.push(...legThrows)
                                    }
                                }
                            }
                        }

                        if (!allThrows.length) return null

                        const totalScore = allThrows.reduce(
                            (sum, throwData) => sum + throwData.score,
                            0,
                        )
                        const totalThrows = allThrows.length

                        console.log(
                            `X01 average: ${totalScore} points ÷ ${totalThrows} throws = ${totalScore / totalThrows}`,
                        )

                        if (totalThrows > 0) {
                            const average = totalScore / totalThrows
                            return Math.round(average * 100) / 100
                        }

                        return null
                    }
                } catch (error) {
                    console.warn('Error getting competitor average:', error)
                    return null
                }
            }
        },
        getStartingScore() {
            return this.getSettings.starting_score || 501
        },
        getCurrentDartNumber() {
            const currentLeg = this.getCurrentLeg
            if (!currentLeg?.turns?.length) return 0

            const currentTurn = currentLeg.turns[currentLeg.turns.length - 1]
            if (!currentTurn?.darts) return 0

            return currentTurn.darts.length
        },

        getCurrentDartNumberForProfile() {
            return (profileId) => {
                const currentLeg = this.getCurrentLeg
                if (!currentLeg?.turns?.length) return 0

                const profiles = this.getProfiles
                const profile = profiles.find((p) => p.id === profileId)
                if (!profile?.team) return 0

                const teamTurns = currentLeg.turns.filter((turn) => turn.team === profile.team)
                if (teamTurns.length > 0) {
                    const lastTeamTurn = teamTurns[teamTurns.length - 1]
                    return lastTeamTurn.darts?.length || 0
                }

                return 0
            }
        },
        isMatchOver: (state) => {
            const winnerId = state.match.matchData?.match_detail_data?.winner_id
            return (
                winnerId !== null && winnerId !== undefined && winnerId !== '' && winnerId !== '0'
            )
        },
        isPlayerWinner: (state) => {
            return (playerId) => {
                const winnerId = state.match.matchData?.match_detail_data?.winner_id
                return winnerId && String(winnerId) === String(playerId)
            }
        },

        isTeamPlay() {
            const profiles = this.getProfiles
            if (!profiles.length) return false

            const teamGroups = {}
            profiles.forEach((profile) => {
                if (profile.team) {
                    if (!teamGroups[profile.team]) {
                        teamGroups[profile.team] = []
                    }
                    teamGroups[profile.team].push(profile)
                }
            })

            return Object.values(teamGroups).some((team) => team.length > 1)
        },
        getTeams() {
            const profiles = this.getProfiles
            if (!profiles.length) return []

            const { t } = useTranslationsStore()

            const teamGroups = {}

            profiles.forEach((profile) => {
                if (profile.team) {
                    if (!teamGroups[profile.team]) {
                        teamGroups[profile.team] = []
                    }
                    teamGroups[profile.team].push(profile)
                }
            })

            return Object.keys(teamGroups)
                .sort((a, b) => {
                    const sizeA = teamGroups[a].length
                    const sizeB = teamGroups[b].length
                    if (sizeA !== sizeB) {
                        return sizeB - sizeA
                    }
                    return a.localeCompare(b)
                })
                .map((teamId, index) => ({
                    id: teamId,
                    team: teamId,
                    name:
                        teamGroups[teamId].length > 1
                            ? t('game_stats.team_label', { n: index + 1 })
                            : teamGroups[teamId][0].tag_name,
                    players: teamGroups[teamId],
                }))
        },

        getCompetitorSetsWon() {
            return (competitorId) => {
                const sets = this.getSets
                if (!sets.length) return 0

                let setsWon = 0
                for (const set of sets) {
                    if (set.winner_id === competitorId) {
                        setsWon++
                    }
                }
                return setsWon
            }
        },

        getCompetitorLegsWon(onlyCurrentSet = false) {
            return (competitorId) => {
                const sets = this.getSets
                if (!sets.length) return 0

                let legsWon = 0
                for (const set of sets) {
                    if (onlyCurrentSet && set !== sets[sets.length - 1]) {
                        continue
                    }

                    if (set.legs) {
                        for (const leg of set.legs) {
                            if (leg.winner_id === competitorId) {
                                legsWon++
                            }
                        }
                    }
                }
                return legsWon
            }
        },

        getMatchTypeId() {
            return this.match?.matchtype_id || this.setup?.matchtype_id
        },

        /** Whether the current match is Play Online (vs. Play Local, single-device). */
        isRemotePlay() {
            return Boolean(this.match?.online || this.setup?.online)
        },

        isBotProfile() {
            return (profileId) => {
                const id = Number(profileId)
                return DARTBOT_PROFILE_IDS.has(id)
            }
        },

        hasBotProfile() {
            const profiles = this.getProfiles
            if (!profiles.length) return false

            for (const profile of profiles) {
                if (this.isBotProfile(profile.profile_id)) {
                    return true
                }
            }
            return false
        },

        isBotTurn() {
            const currentPlayer = this.getCurrentPlayer
            if (!currentPlayer) return false
            return this.isBotProfile(currentPlayer.id)
        },

        isTurnFromBot() {
            return (turn) => {
                if (!turn) return false

                if (turn.profile_id) {
                    return this.isBotProfile(turn.profile_id)
                }

                if (turn.team) {
                    const teamId = turn.team
                    const teamIdNum = Number(teamId)

                    if (!isNaN(teamIdNum) && DARTBOT_PROFILE_IDS.has(teamIdNum)) {
                        return true
                    }

                    const profiles = this.getProfiles
                    const teamProfiles = profiles.filter((profile) => profile.team === teamId)
                    return teamProfiles.some((profile) => {
                        const profileId = profile.profile_id || profile.id
                        return this.isBotProfile(profileId)
                    })
                }

                return false
            }
        },
    },

    actions: {
        _captureError(error, context = {}) {
            const matchId = this.match?.id || this.setup?.id
            Sentry.withScope((scope) => {
                scope.setTag('store', 'match')
                if (matchId) {
                    scope.setTag('matchId', String(matchId))
                }
                if (context.tags) {
                    Object.entries(context.tags).forEach(([key, value]) => {
                        scope.setTag(key, value)
                    })
                }
                scope.setExtra('matchState', {
                    matchId: this.match?.id,
                    setupId: this.setup?.id,
                    matchtypeId: this.match?.matchtype_id || this.setup?.matchtype_id,
                    playMode: this.playMode,
                })
                if (context.extra) {
                    Object.entries(context.extra).forEach(([key, value]) => {
                        scope.setExtra(key, value)
                    })
                }
                if (context.level) {
                    scope.setLevel(context.level)
                }
                Sentry.captureException(error)
            })
        },
        getCompetitorThrowsFromLeg(leg, competitorId) {
            if (!leg?.turns?.length) return []

            const competitorTurns = leg.turns.filter((turn) => turn.team === competitorId)

            if (!competitorTurns.length) return []

            const completedTurns = competitorTurns.filter((turn) => {
                return turn.thrown_score !== undefined && turn.thrown_score !== -1
            })

            return completedTurns.map((turn, turnIndex) => ({
                score: turn.thrown_score,
                remaining_score: turn.remaining_score,
                darts: turn.darts || [],
                turnIndex: turnIndex,
                fullTurn: turn,
            }))
        },
        getCompetitorDartsThrownFromLeg(leg, competitorId) {
            if (!leg?.turns?.length) return 0

            const competitorTurns = leg.turns.filter((turn) => turn.team === competitorId)

            return competitorTurns.reduce((total, turn) => {
                const thrown = turn.darts_thrown || 0
                return total + (thrown === -1 ? 0 : thrown)
            }, 0)
        },
        setEditingTurn(competitorId, turnIndex, turnData) {
            const sets = this.getSets
            if (!sets.length) return

            const currentSet = sets[sets.length - 1]
            if (!currentSet?.legs?.length) return

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]

            const competitorTurns = currentLeg.turns.filter((turn) => turn.team === competitorId)
            const competitorTurn = competitorTurns[turnIndex]

            if (!competitorTurn) return

            if (this.isTurnFromBot(competitorTurn)) {
                return
            }

            const actualTurnIndex = currentLeg.turns.findIndex((t) => t === competitorTurn)

            this.editingTurn = {
                set_index: sets.length - 1,
                leg_index: currentSet.legs.length - 1,
                turn_index: actualTurnIndex,
                competitor_turn_index: turnIndex,
                thrown_score: turnData.score,
                darts: turnData.darts || [],
                competitorId: competitorId,
                profile_id: competitorTurn.profile_id,
            }

            // One event per correction the player starts, not per dart they then retype - how
            // often scores have to be fixed is a score-entry UX signal, and the per-dart writes
            // that follow would drown it.
            analytics.trackEvent(ANALYTICS_EVENTS.TURN_EDITED, {
                thrown_score: turnData.score,
                turn_index: turnIndex,
            })
        },
        clearEditingTurn() {
            this.editingTurn = null
        },
        isEditingTurn(competitorId, turnIndex) {
            if (!this.editingTurn) return false
            return (
                this.editingTurn.competitorId === competitorId &&
                this.editingTurn.competitor_turn_index === turnIndex
            )
        },
        async createMatch(matchPayload = null) {
            const payload = matchPayload || {
                matchtype_id: this.setup.matchtype_id,
                min_players: this.setup.min_players,
                max_players: this.setup.max_players,
                name: this.setup.name,
                matchmaking: this.setup.matchmaking,
                online: this.setup.online,
                settings: this.setup.settings,
                include_in_stats: this.setup.include_in_stats,
            }

            try {
                const matchData = await apiService.post('/api/v1/matches', payload)

                console.log('Match created successfully:', matchData.match)

                this.setup.id = matchData.match.id
                this.setup.matchtype_id = matchData.match.matchtype_id
                this.setup.profiles = matchData.match.profiles
                this.setup.settings = matchData.match.settings
                this.setup.include_in_stats = matchData.match.include_in_stats ?? true
                this.setup.creator_profile_id = matchData.match.creator_profile_id ?? null
                this.setup.online = matchData.match.online ?? this.setup.online

                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_CREATED, {
                    match_id: String(this.setup.id),
                    game_type: matchTypeName(this.setup.matchtype_id, 'Unknown'),
                    online: !!this.setup.online,
                    player_count: this.setup.profiles?.length ?? 0,
                })

                this.joinMatchChannel(this.setup.id)

                return matchData
            } catch (error) {
                console.error('Failed to create match:', error)
                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_CREATE_FAILED, {
                    game_type: matchTypeName(payload.matchtype_id, 'Unknown'),
                    online: !!payload.online,
                    reason: error?.message ?? 'unknown',
                })
                this._captureError(error, {
                    tags: { action: 'createMatch' },
                    extra: { payload },
                })
                throw error
            }
        },

        async createMatchFromMatchtype(matchtype, online = false) {
            if (!matchtype) {
                throw new Error('Matchtype is required')
            }

            if (this.isCreatingMatch) {
                console.warn('Match creation already in progress')
                throw new Error('Match creation already in progress')
            }

            this.isCreatingMatch = true

            try {
                // Leave the WebSocket channel synchronously BEFORE clearing state.
                // clearMatch() defers leaveMatchChannel via setTimeout, which allows
                // stale WS messages to repopulate match.matchData during the async
                // createMatch() call. Leaving first prevents that race.
                this.leaveMatchChannel()
                this.clearMatch()

                this.setup.matchtype_id = matchtype.id
                this.setup.settings = matchtype.default_settings
                this.setup.online = online

                await this.createMatch()
                return this.setup.id
            } catch (error) {
                console.error('Failed to create match from matchtype:', error)
                this._captureError(error, {
                    tags: { action: 'createMatchFromMatchtype' },
                    extra: { matchtypeId: matchtype?.id, matchtypeName: matchtype?.name },
                })
                throw error
            } finally {
                this.isCreatingMatch = false
            }
        },

        async selectMatchType(matchTypeName, authStore, router, toast, online = false) {
            const { t } = useTranslationsStore()

            // Find matchtype by name
            const matchtype = authStore.matchtypes.find((mt) => mt.name === matchTypeName)

            if (!matchtype) {
                toast(t('general.something_went_wrong'), 5000, t('match_errors.matchtype_not_found'))
                console.error(`Matchtype "${matchTypeName}" not found`)
                throw new Error(`Matchtype "${matchTypeName}" not found`)
            }

            if (this.isCreatingMatch) {
                console.warn('Match creation already in progress')
                return
            }

            // `matchTypeName` here is this action's own parameter (the matchtype name as picked in
            // the UI), which shadows the imported helper of the same name - it is already the
            // string we want.
            analytics.trackEvent(ANALYTICS_EVENTS.GAME_TYPE_SELECTED, {
                game_type: matchTypeName,
                online: !!online,
            })

            try {
                const matchId = await this.createMatchFromMatchtype(matchtype, online)
                router.push({
                    name: 'match-setup',
                    params: { matchId },
                })
            } catch (error) {
                console.error('Failed to select match type:', error)
                if (error.message !== 'Match creation already in progress') {
                    toast(t('general.something_went_wrong'), 5000, t('match_errors.create_match_failed'))
                }
                throw error
            }
        },

        async rematch(matchId) {
            try {
                const response = await apiService.post(`/api/v1/matches/${matchId}/rematch`)

                if (response.success) {
                    // The strongest retention signal the app has: they finished one and wanted
                    // another straight away.
                    analytics.trackEvent(ANALYTICS_EVENTS.REMATCH_STARTED, {
                        previous_match_id: String(matchId),
                        match_id: String(response.match.id),
                    })

                    this.clearMatch()
                    await this.restoreMatchFromAPI(response.match.id)

                    return response.match
                }

                throw new Error('Rematch failed')
            } catch (error) {
                console.error('Failed to create rematch:', error)
                this._captureError(error, {
                    tags: { action: 'rematch' },
                    extra: { matchId },
                })
                throw error
            }
        },

        async cancelMatch(matchId) {
            const authStore = useAuthStore()
            try {
                this.leaveMatchChannel()
                let response = null
                // In a same-session create→start→play flow the match is never
                // reloaded from the API, so match.creator_profile_id can be null.
                // Fall back to the setup so the creator still cancels (deletes the
                // whole match) rather than only leaving it — which would strand the
                // match as an "ongoing" game for the remaining players.
                const creatorProfileId =
                    this.match.creator_profile_id ?? this.setup.creator_profile_id
                const isCreator = creatorProfileId == authStore.profile?.id
                console.log(
                    'Canceling match:',
                    matchId,
                    'should cancel instead of leave',
                    isCreator,
                )
                if (isCreator) {
                    response = await apiService.post(`/api/v1/matches/${matchId}/cancel`)
                } else {
                    response = await apiService.post(`/api/v1/matches/${matchId}/leave`, {
                        profile_id: authStore.profile?.id,
                    })
                }

                if (response.success) {
                    analytics.trackEvent(ANALYTICS_EVENTS.MATCH_CANCELLED, {
                        by: isCreator ? 'host' : 'player',
                        // Which screen the user bailed from - the lobby and a running match are
                        // very different failures, and this action serves both.
                        phase: analytics.currentScreen ?? 'unknown',
                        online: !!(this.match.online || this.setup.online),
                    })

                    this.clearMatch()
                    return response
                }

                throw new Error('Cancel failed')
            } catch (error) {
                console.error('Failed to cancel match:', error)
                this.leaveMatchChannel()
                throw error
            }
        },

        async leaveMatch(matchId, profileId) {
            if (!matchId || !profileId) {
                throw new Error('Match ID and profile ID are required to leave a match')
            }

            try {
                const response = await apiService.post(`/api/v1/matches/${matchId}/leave`, {
                    profile_id: profileId,
                })

                return response
            } catch (error) {
                console.error('Failed to leave match:', error)
                this._captureError(error, {
                    tags: { action: 'leaveMatch' },
                    extra: { matchId, profileId },
                })
                throw error
            }
        },

        async endMatch(matchId) {
            try {
                const response = await apiService.post(`/api/v1/matches/${matchId}/end`)

                if (response.success) {
                    return response
                }

                throw new Error('End match failed')
            } catch (error) {
                console.error('Failed to end match:', error)
                throw error
            }
        },

        async updateMatch() {
            try {
                const matchPayload = {
                    matchtype_id: this.setup.matchtype_id,
                    min_players: this.setup.min_players,
                    max_players: this.setup.max_players,
                    name: this.setup.name,
                    matchmaking: this.setup.matchmaking,
                    settings: this.setup.settings,
                    dart_setup_id: this.setup.dart_setup_id,
                    include_in_stats: this.setup.include_in_stats,
                    profiles: this.setup.profiles,
                }

                const matchData = await apiService.put(
                    `/api/v1/matches/${this.setup.id}`,
                    matchPayload,
                )

                console.log('Match created successfully:', matchData.match)

                this.setup.id = matchData.match.id
                this.setup.matchtype_id = matchData.match.matchtype_id
                this.setup.profiles = matchData.match.profiles
                this.setup.settings = matchData.match.settings
                this.setup.include_in_stats = matchData.match.include_in_stats ?? true
                this.setup.creator_profile_id = matchData.match.creator_profile_id ?? null

                return matchData
            } catch (error) {
                console.error('Failed to create match:', error)
                throw error
            }
        },

        /**
         * Persist the calling player's own dart setup for the current match.
         * Works for every participant (not just the creator), so invited
         * players can change their setup from the lobby.
         */
        async updateOwnDartSetup(setupId) {
            const matchId = this.setup.id || this.match.id
            if (!matchId) {
                throw new Error('No match created yet')
            }

            this.setup.dart_setup_id = setupId

            try {
                const response = await apiService.patch(
                    `/api/v1/matches/${matchId}/dart-setup`,
                    { dart_setup_id: setupId },
                )

                // Ties hardware to performance: with match_finished this answers "how do players
                // using setup X actually score", which is the whole point of having setups.
                analytics.trackEvent(ANALYTICS_EVENTS.SETUP_APPLIED_TO_MATCH, {
                    setup_id: String(setupId),
                })

                return response
            } catch (error) {
                console.error('Failed to update dart setup:', error)
                this._captureError(error, {
                    tags: { action: 'updateOwnDartSetup' },
                    extra: { matchId, setupId },
                })
                throw error
            }
        },

        joinMatchChannel(matchId) {
            if (!matchId) {
                console.warn('Cannot join match channel: no match ID provided')
                return
            }

            if (this.isJoiningChannel) {
                return
            }

            this.clearEditingTurn()
            this.matchEndedRemotely = null

            const channelName = `match.${matchId}`
            const currentMatchId = this.match.id || this.setup.id

            if (this.activeChannelName === channelName) {
                return
            }

            if (currentMatchId && currentMatchId !== matchId) {
                this.leaveMatchChannel()
            }

            if (!echoService.instance) {
                console.error('Echo service not initialized')
                return
            }

            this.isJoiningChannel = true

            try {
                if (this.activeChannel) {
                    this.leaveMatchChannel()
                }

                echoService.setAllowedMatchChannelValidator((matchIdToCheck) => {
                    const activeMatchId = this.match.id || this.setup.id
                    return !activeMatchId || activeMatchId === matchIdToCheck
                })

                console.log('Joining private channel:', channelName)

                this.activeChannelName = channelName
                this.activeChannel = echoService.instance.private(channelName)

                if (window.__memoryLeakDetector) {
                    window.__memoryLeakDetector.trackEchoChannel(channelName)
                }

                this.activeChannel.subscribed(() => {
                    console.log('Successfully subscribed to match channel:', channelName)
                })

                this.activeChannel.error((error) => {
                    console.error('Channel subscription error:', error)

                    const errorChannelName = error?.channel || error?.channel_name
                    const isCurrentChannel =
                        !errorChannelName ||
                        errorChannelName === channelName ||
                        errorChannelName === `private-${channelName}`

                    if (
                        isCurrentChannel &&
                        (error?.type === 'AuthError' ||
                            error?.message?.includes('Authentication failed') ||
                            error?.message?.includes('403') ||
                            error?.status === 403)
                    ) {
                        console.warn(
                            'Access denied to current match channel - clearing match state',
                            { errorChannelName, channelName },
                        )
                        this.clearMatch()
                    } else if (!isCurrentChannel) {
                        console.warn('Auth error for non-active channel, ignoring:', {
                            errorChannelName,
                            currentChannel: channelName,
                        })
                    }
                })

                this.activeChannel.listen('.match.updated', (data) => {
                    this.handleMatchUpdate(data)
                })

                this.activeChannel.listen('.rt-match.updated', (data) => {
                    this.handleRtMatchUpdate(data)
                })

                this.activeChannel.listen('.match.ended', (data) => {
                    this.handleMatchEnded(data)
                })

                this.startWebsocketHealthMonitor()

                console.log('Channel setup complete for:', channelName)
            } catch (error) {
                console.error('Failed to join match channel:', error)
                this._captureError(error, {
                    tags: { action: 'joinMatchChannel', errorType: 'websocket' },
                    extra: { matchId, channelName },
                })
            } finally {
                this.isJoiningChannel = false
            }
        },

        leaveMatchChannel() {
            this.stopWebsocketHealthMonitor()
            this.clearRtQueueAndCancelFlush()
            this.clearEditingTurn()

            if (this.activeChannel) {
                const matchId = this.match.id || this.setup.id
                const channelName = this.activeChannelName || (matchId ? `match.${matchId}` : null)
                console.log('Leaving match channel for match:', matchId, 'channel:', channelName)
                try {
                    if (channelName && echoService.instance) {
                        this.activeChannel.stopListening('.match.updated')
                        this.activeChannel.stopListening('.rt-match.updated')
                        this.activeChannel.stopListening('.match.ended')

                        echoService.unsubscribe(channelName)

                        if (window.__memoryLeakDetector) {
                            window.__memoryLeakDetector.untrackEchoChannel(channelName)
                        }
                    }
                } catch (error) {
                    console.error('Error leaving match channel:', error)
                }
                this.activeChannel = null
                this.activeChannelName = null
            }

            this.isJoiningChannel = false
        },

        handleMatchUpdate(data) {
            console.log('Raw websocket data received:', data)

            if (data.profiles && Array.isArray(data.profiles)) {
                this.setup.profiles = data.profiles
            }
        },

        handleMatchEnded(data) {
            const endedMatchId = data?.match_id
            const currentMatchId = this.match.id || this.setup.id

            // Ignore stray events for a match we are no longer in.
            if (endedMatchId && currentMatchId && String(endedMatchId) !== String(currentMatchId)) {
                return
            }

            console.log('Match ended remotely:', data)

            this.matchEndedRemotely = {
                matchId: endedMatchId ?? currentMatchId,
                reason: data?.reason || 'ended',
                endedBy: data?.ended_by || null,
            }
        },

        clearMatchEndedRemotely() {
            this.matchEndedRemotely = null
        },

        getBotTurnMetaFromSnapshot(data) {
            const winnerId = data?.rt_matchdata?.match_detail_data?.winner_id
            if (
                winnerId !== null &&
                winnerId !== undefined &&
                winnerId !== '' &&
                winnerId !== '0'
            ) {
                return null
            }

            const currentTurnEntry = resolveCurrentTurnEntry(
                data?.rt_gatekeeper,
                data?.rt_matchdata?.match_detail_data,
            )
            if (!currentTurnEntry) return null

            const profiles = data?.rt_matchdata?.match_detail_data?.profiles || []
            const currentProfile = profiles.find(
                (profile) => profile?.id === currentTurnEntry.profile_id,
            )
            if (!currentProfile) return null

            const profileId = currentProfile.profile_id || currentProfile.id
            if (!this.isBotProfile(profileId)) return null

            const sets = data?.rt_matchdata?.match_detail_data?.sets || []
            const currentSet = sets[sets.length - 1]
            const currentLeg = currentSet?.legs?.[currentSet.legs.length - 1]
            const turns = currentLeg?.turns || []
            const teamId = currentProfile.team
            const teamTurns = turns.filter((turn) => String(turn.team) === String(teamId))
            const lastTeamTurn = teamTurns[teamTurns.length - 1]
            const dartsCount = Array.isArray(lastTeamTurn?.darts) ? lastTeamTurn.darts.length : 0
            const setIndex = Math.max(0, sets.length - 1)
            const legIndex = Math.max(0, (currentSet?.legs?.length || 1) - 1)
            const turnNumber = Math.max(1, teamTurns.length)
            const turnKey = `${teamId}:${setIndex}:${legIndex}:${turnNumber}`
            const snapshotKey = `${turnKey}:${dartsCount}:${lastTeamTurn?.thrown_score ?? ''}:${lastTeamTurn?.remaining_score ?? ''}`

            return { teamId, turnKey, snapshotKey }
        },

        setBotThrowIndicator(teamId, step, turnKey = null, snapshotKey = null) {
            const normalizedStep = Math.min(3, Math.max(1, step))
            if (
                this.botThrowIndicator.visible &&
                String(this.botThrowIndicator.teamId) === String(teamId) &&
                this.botThrowIndicator.step === normalizedStep &&
                this.botThrowIndicator.turnKey === turnKey &&
                this.botThrowIndicator.lastSnapshotKey === snapshotKey
            ) {
                return
            }
            this.botThrowIndicator.visible = true
            this.botThrowIndicator.teamId = teamId
            this.botThrowIndicator.step = normalizedStep
            this.botThrowIndicator.turnKey = turnKey
            this.botThrowIndicator.lastSnapshotKey = snapshotKey
            console.log(`${BOT_REPLAY_LOG_PREFIX} indicator:set`, {
                visible: this.botThrowIndicator.visible,
                teamId: this.botThrowIndicator.teamId,
                step: this.botThrowIndicator.step,
                turnKey: this.botThrowIndicator.turnKey,
                snapshotKey: this.botThrowIndicator.lastSnapshotKey,
            })
        },

        clearBotThrowIndicator() {
            if (
                this.botThrowIndicator.visible ||
                this.botThrowIndicator.teamId ||
                this.botThrowIndicator.step
            ) {
                console.log(`${BOT_REPLAY_LOG_PREFIX} indicator:clear`, {
                    visible: this.botThrowIndicator.visible,
                    teamId: this.botThrowIndicator.teamId,
                    step: this.botThrowIndicator.step,
                    turnKey: this.botThrowIndicator.turnKey,
                    snapshotKey: this.botThrowIndicator.lastSnapshotKey,
                })
            }
            this.botThrowIndicator.visible = false
            this.botThrowIndicator.step = 0
            this.botThrowIndicator.teamId = null
            this.botThrowIndicator.turnKey = null
            this.botThrowIndicator.lastSnapshotKey = null
        },

        updateBotThrowIndicatorFromSnapshot(data) {
            const meta = this.getBotTurnMetaFromSnapshot(data)
            if (!meta) {
                this.clearBotThrowIndicator()
                return
            }
            if (
                !this.botThrowIndicator.visible ||
                String(this.botThrowIndicator.teamId) !== String(meta.teamId) ||
                this.botThrowIndicator.turnKey !== meta.turnKey
            ) {
                this.setBotThrowIndicator(meta.teamId, 1, meta.turnKey, meta.snapshotKey)
                return
            }

            if (this.botThrowIndicator.lastSnapshotKey === meta.snapshotKey) {
                return
            }

            const nextStep = Math.min(3, this.botThrowIndicator.step + 1)
            this.setBotThrowIndicator(meta.teamId, nextStep, meta.turnKey, meta.snapshotKey)
        },

        /**
         * Report a completed match.
         *
         * Deliberately no averages or checkout percentages: those are computed (and displayed)
         * from the same snapshot by GameStatsView, and a second implementation here would drift
         * from the numbers the player is shown. Shape and duration only - the detail is already
         * reconstructable from the match itself.
         */
        trackMatchFinished(rtMatchData) {
            const detail = rtMatchData?.match_detail_data || {}
            const sets = detail.sets || []

            analytics.trackEvent(ANALYTICS_EVENTS.MATCH_FINISHED, {
                match_id: String(this.match.id ?? this.setup.id ?? ''),
                game_type: matchTypeName(
                    this.match.matchtype_id ?? this.setup.matchtype_id,
                    'Unknown',
                ),
                online: !!(this.match.online || this.setup.online),
                player_count: (detail.profiles || []).length,
                sets_played: sets.length,
                legs_played: sets.reduce((total, set) => total + (set.legs?.length || 0), 0),
                vs_bot: this.hasBotProfile,
                ...(this.matchStartedAt
                    ? { duration_sec: Math.round((Date.now() - this.matchStartedAt) / 1000) }
                    : {}),
            })

            this.matchStartedAt = null
        },

        emitAnnouncerEvent(eventName, eventData = {}) {
            try {
                if (!window.$serviceBus) {
                    console.warn('Match store: $serviceBus not available')
                    return
                }
                const matchTypeId = this.match?.matchtype_id || this.setup?.matchtype_id
                if (!ANNOUNCER_ENABLED_MATCH_TYPES.has(Number(matchTypeId))) {
                    return
                }
                window.$serviceBus.emit(`announcer:${eventName}`, eventData)
                console.log(`Match store: Emitted announcer event ${eventName}`, eventData)
            } catch (error) {
                console.warn(`Match store: Failed to emit event ${eventName}`, error)
            }
        },

        applyRtMatchSnapshot(data, options = {}) {
            const { updateIndicator = true } = options

            // --- Announcer: capture current state BEFORE applying snapshot ---
            const prevSets = this.match.matchData?.match_detail_data?.sets || []
            const prevCurrentSet = prevSets[prevSets.length - 1]
            const prevLegCount = prevCurrentSet?.legs?.length || 0
            const prevSetCount = prevSets.length

            const prevWinnerId = this.match.matchData?.match_detail_data?.winner_id
            const prevHasWinner =
                prevWinnerId !== null &&
                prevWinnerId !== undefined &&
                prevWinnerId !== '' &&
                prevWinnerId !== '0'

            // --- Apply snapshot ---
            const rtMatchData =
                data?.rt_matchdata && typeof data.rt_matchdata === 'object' ? data.rt_matchdata : {}
            this.match.matchData = {
                ...rtMatchData,
                match_detail_data: rtMatchData.match_detail_data ?? {},
            }
            this.match.gatekeeper = data.rt_gatekeeper
            if (updateIndicator) {
                this.updateBotThrowIndicatorFromSnapshot(data)
            }

            // --- Announcer: compare new state and emit events ---
            const newSets = rtMatchData.match_detail_data?.sets || []
            const newCurrentSet = newSets[newSets.length - 1]
            const newLegCount = newCurrentSet?.legs?.length || 0
            const newSetCount = newSets.length

            const newWinnerId = rtMatchData.match_detail_data?.winner_id
            const newHasWinner =
                newWinnerId !== null &&
                newWinnerId !== undefined &&
                newWinnerId !== '' &&
                newWinnerId !== '0'

            // 1. Winner announced
            if (!prevHasWinner && newHasWinner) {
                this.emitAnnouncerEvent('matchEnd', { winnerId: newWinnerId })
                this.trackMatchFinished(rtMatchData)
            }
            // 2. New leg started (leg count increased within same set, or a new set started with >1 legs possible)
            else if (
                newSetCount > prevSetCount ||
                (newSetCount === prevSetCount && newLegCount > prevLegCount)
            ) {
                const totalLegsInSet = newCurrentSet?.legs?.length || 1
                // Don't announce the very first leg of the match (leg 1 of set 1)
                if (newSetCount === 1 && totalLegsInSet === 1) {
                    // skip - matchStart already covers this
                } else {
                    const settings = rtMatchData.match_detail_data?.settings || {}
                    const legsToWin = settings.legs_to_win || 1
                    const setsToWin = settings.sets_to_win || 1

                    let isFinal = false
                    const profiles = rtMatchData.match_detail_data?.profiles || []
                    const teamIds = [...new Set(profiles.map((p) => p.team).filter(Boolean))]

                    // Count sets won by each team (by looking at all completed sets)
                    const setsWon = {}
                    newSets.forEach((set) => {
                        if (set.winner_id) {
                            setsWon[set.winner_id] = (setsWon[set.winner_id] || 0) + 1
                        }
                    })

                    // Check if any team is one set away from winning the match
                    const isMatchPoint = Object.values(setsWon).some(
                        (count) => count === setsToWin - 1,
                    )

                    // Final leg only if: (1) match is at match point AND (2) any team is one leg away in THIS set
                    if (isMatchPoint) {
                        isFinal = teamIds.some((teamId) => {
                            // Count completed legs won by this team (legs with a winner_id)
                            const legsWon = (newCurrentSet?.legs || []).filter(
                                (leg) => leg.winner_id === teamId,
                            ).length
                            return legsWon === legsToWin - 1
                        })
                    }

                    this.emitAnnouncerEvent(isFinal ? 'finalLeg' : 'nextLeg', {})

                    // A new leg (or set) beginning is how a finished one is observed - the
                    // snapshot carries no "leg ended" flag of its own.
                    //
                    // prevSetCount 0 means this is the first snapshot the store has seen, not a
                    // transition: restoring a match that is already in progress would otherwise
                    // report a leg or set finishing that nobody just played.
                    if (prevSetCount > 0) {
                        const isNewSet = newSetCount > prevSetCount
                        analytics.trackEvent(
                            isNewSet
                                ? ANALYTICS_EVENTS.SET_FINISHED
                                : ANALYTICS_EVENTS.LEG_FINISHED,
                            isNewSet
                                ? { set_index: prevSetCount, legs_in_set: prevLegCount }
                                : {
                                      set_index: newSetCount,
                                      leg_index: prevLegCount,
                                      is_final_leg: isFinal,
                                  },
                        )
                    }
                }
            }
            // 3. Turn completed. The match processor appends a fresh pending
            //    turn after every finished turn, so a turn is done exactly when
            //    the current leg gains a turn — and the finished turn is the one
            //    right before the new pending turn. Detecting completion by turn
            //    count (rather than by thrown_score leaving its -1 seed) avoids
            //    announcing the in-progress turn, whose thrown_score updates on
            //    every dart. This still works in solo games where the same
            //    player throws every turn.
            else if (!newHasWinner) {
                const prevCurrentLeg = prevCurrentSet?.legs?.[prevCurrentSet.legs.length - 1]
                const newCurrentLeg = newCurrentSet?.legs?.[newCurrentSet.legs.length - 1]

                const prevTurnCount = prevCurrentLeg?.turns?.length || 0
                const newTurns = newCurrentLeg?.turns || []

                if (newTurns.length > prevTurnCount && newTurns.length >= 2) {
                    const finishedTurn = newTurns[newTurns.length - 2]
                    this.emitAnnouncerEvent('turnEnd', {
                        score: finishedTurn.thrown_score,
                        teamId: finishedTurn.team,
                    })

                    // Per-turn events would mean hundreds per match; only the turns a player
                    // would actually tell someone about are worth reporting.
                    if (finishedTurn.thrown_score >= HIGHLIGHT_SCORE_THRESHOLD) {
                        analytics.trackEvent(ANALYTICS_EVENTS.HIGHLIGHT_SCORED, {
                            score: finishedTurn.thrown_score,
                            by_bot: this.isBotProfile(finishedTurn.profile_id),
                        })
                    }
                }
            }
        },

        applyAuthoritativeRtMatchSnapshot(data) {
            this.cancelRtFlush({ clearIndicator: false })
            this.applyRtMatchSnapshot(data)
        },

        setRealtimePause(isPaused) {
            if (this.isRealtimePaused === isPaused) return
            this.isRealtimePaused = isPaused
            console.log(`${BOT_REPLAY_LOG_PREFIX} pause:${isPaused ? 'on' : 'off'}`, {
                queueSize: this.rtUpdateQueue.length,
                isFlushingQueue: this.isFlushingQueue,
            })
            if (isPaused) this.clearBotThrowIndicator()
        },

        cancelRtFlush(options = {}) {
            const { clearIndicator = true } = options
            if (this.rtFlushTimer) {
                clearTimeout(this.rtFlushTimer)
                this.rtFlushTimer = null
            }
            console.log(`${BOT_REPLAY_LOG_PREFIX} flush:cancel`, {
                queueSize: this.rtUpdateQueue.length,
                isFlushingQueue: this.isFlushingQueue,
                clearIndicator,
            })
            this.isFlushingQueue = false
            this.rtUpdateQueue = []
            this.pendingRtSnapshot = null
            if (clearIndicator) this.clearBotThrowIndicator()
        },

        clearRtQueueAndCancelFlush() {
            this.cancelRtFlush()
            this.isRealtimePaused = false
        },

        enqueueRtMatchUpdate(data) {
            if (this.rtUpdateQueue.length >= RT_UPDATE_QUEUE_MAX_SIZE) this.rtUpdateQueue.shift()
            this.rtUpdateQueue.push(data)
            console.log(`${BOT_REPLAY_LOG_PREFIX} queue:push`, {
                queueSize: this.rtUpdateQueue.length,
                winnerId: data?.rt_matchdata?.match_detail_data?.winner_id,
            })
        },

        flushQueuedRtUpdatesWithSpacing(intervalMs = RT_FLUSH_INTERVAL_MS) {
            if (this.isFlushingQueue) return
            if (!this.rtUpdateQueue.length) {
                this.isRealtimePaused = false
                this.rtFlushTimer = null
                this.clearBotThrowIndicator()
                console.log(`${BOT_REPLAY_LOG_PREFIX} flush:skip-empty`)
                return
            }

            const firstReplayMeta = this.getBotTurnMetaFromSnapshot(this.rtUpdateQueue[0])
            const replayTeamId = firstReplayMeta?.teamId ?? null
            let processed = 0

            this.isFlushingQueue = true
            this.isRealtimePaused = false
            console.log(`${BOT_REPLAY_LOG_PREFIX} flush:start`, {
                queueSize: this.rtUpdateQueue.length,
                replayTeamId,
                intervalMs,
            })
            if (replayTeamId !== null) {
                this.setBotThrowIndicator(replayTeamId, 1, 'replay', `replay:0`)
            } else {
                this.clearBotThrowIndicator()
            }

            const drain = () => {
                if (!this.rtUpdateQueue.length) {
                    this.isFlushingQueue = false
                    this.rtFlushTimer = null
                    this.clearBotThrowIndicator()
                    if (this.pendingRtSnapshot) {
                        console.log(`${BOT_REPLAY_LOG_PREFIX} flush:apply-pending-after-empty`)
                        this.applyRtMatchSnapshot(this.pendingRtSnapshot)
                        this.pendingRtSnapshot = null
                    }
                    console.log(`${BOT_REPLAY_LOG_PREFIX} flush:done-empty`)
                    return
                }
                const next = this.rtUpdateQueue.shift()
                processed++
                console.log(`${BOT_REPLAY_LOG_PREFIX} flush:apply`, {
                    processed,
                    remaining: this.rtUpdateQueue.length,
                    replayTeamId,
                })
                this.applyRtMatchSnapshot(next, { updateIndicator: false })
                if (!this.rtUpdateQueue.length) {
                    this.isFlushingQueue = false
                    this.rtFlushTimer = null
                    this.clearBotThrowIndicator()
                    if (this.pendingRtSnapshot) {
                        console.log(`${BOT_REPLAY_LOG_PREFIX} flush:apply-pending-after-replay`, {
                            processed,
                        })
                        this.applyRtMatchSnapshot(this.pendingRtSnapshot)
                        this.pendingRtSnapshot = null
                    }
                    console.log(`${BOT_REPLAY_LOG_PREFIX} flush:done-after-apply`, { processed })
                    return
                }
                if (replayTeamId !== null) {
                    this.setBotThrowIndicator(
                        replayTeamId,
                        processed + 1,
                        'replay',
                        `replay:${processed}`,
                    )
                }
                this.rtFlushTimer = setTimeout(drain, intervalMs)
            }
            this.rtFlushTimer = setTimeout(drain, intervalMs)
        },

        handleRtMatchUpdate(data) {
            if (!this.activeChannel) return
            console.log(`${BOT_REPLAY_LOG_PREFIX} ws:update`, {
                paused: this.isRealtimePaused,
                flushing: this.isFlushingQueue,
                queueSize: this.rtUpdateQueue.length,
                winnerId: data?.rt_matchdata?.match_detail_data?.winner_id,
            })
            if (this.isRealtimePaused) {
                this.enqueueRtMatchUpdate(data)
                return
            }
            if (this.isFlushingQueue) {
                this.pendingRtSnapshot = data
                console.log(`${BOT_REPLAY_LOG_PREFIX} flush:stash-latest`, {
                    queueSize: this.rtUpdateQueue.length,
                })
                return
            }
            this.applyRtMatchSnapshot(data)
        },

        async startMatch() {
            if (!this.setup.id) {
                throw new Error('No match created yet')
            }

            console.log('Starting match:', this.setup.id)

            try {
                const payload = {
                    starting_player: this.setup.starting_player,
                    player_targets: this.setup.player_targets,
                }
                if (this.setup.team_numbers) {
                    payload.team_numbers = this.setup.team_numbers
                }
                const data = await apiService.post(
                    `/api/v1/matches/${this.setup.id}/start`,
                    payload,
                )

                this.match.id = data.match_id
                this.match.matchtype_id = data.matchtype_id
                this.match.online = this.setup.online
                this.match.creator_profile_id =
                    this.setup.creator_profile_id ?? this.match.creator_profile_id
                this.applyAuthoritativeRtMatchSnapshot(data)

                console.log('Match started successfully:', data)

                this.matchStartedAt = Date.now()
                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_STARTED, {
                    match_id: String(this.setup.id),
                    game_type: matchTypeName(this.setup.matchtype_id, 'Unknown'),
                    online: !!this.setup.online,
                    player_count: this.setup.profiles?.length ?? 0,
                    play_mode: this.playMode,
                    sets_to_win: this.setup.settings?.sets_to_win ?? null,
                    legs_to_win: this.setup.settings?.legs_to_win ?? null,
                })

                this.emitAnnouncerEvent('matchStart', { matchId: this.setup.id })

                return data
            } catch (error) {
                console.error('Failed to start match:', error)
                this._captureError(error, {
                    tags: { action: 'startMatch' },
                    extra: {
                        matchId: this.setup.id,
                        startingPlayer: this.setup.starting_player,
                    },
                })
                throw error
            }
        },

        async addGuestToMatch(guestName) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!guestName || !guestName.trim()) {
                throw new Error(useTranslationsStore().t('match_errors.guest_name_required'))
            }

            console.log('Adding guest to match:', { matchId: this.setup.id, guestName })

            try {
                const response = await apiService.post(
                    `/api/v1/matches/${this.setup.id}/join-guest`,
                    {
                        tag_name: guestName.trim(),
                    },
                )

                this.setup.profiles = response.profiles

                analytics.trackEvent(ANALYTICS_EVENTS.LOBBY_PLAYER_ADDED, {
                    player_type: 'guest',
                    player_count: this.setup.profiles?.length ?? 0,
                })

                return response
            } catch (error) {
                console.error('Failed to add guest to match:', error)

                if (error.response?.status === 409) {
                    const { t } = useTranslationsStore()
                    const errorMessage = error.response?.data?.error || t('match_errors.no_guest_slots')
                    throw new Error(errorMessage)
                }

                throw error
            }
        },

        async addDartbotToMatch(bot) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!bot) {
                throw new Error('Bot profile ID is required')
            }

            console.log('Adding dartbot to match:', { matchId: this.setup.id, botName: bot.name })

            try {
                const response = await apiService.post(`/api/v1/matches/${this.setup.id}/add-bot`, {
                    bot_id: bot.id,
                })

                this.setup.profiles = response.profiles

                analytics.trackEvent(ANALYTICS_EVENTS.LOBBY_PLAYER_ADDED, {
                    player_type: 'bot',
                    bot_id: bot.id,
                    player_count: this.setup.profiles?.length ?? 0,
                })

                return response
            } catch (error) {
                console.error('Failed to add bot to match:', error)

                if (error.response?.status === 409) {
                    const { t } = useTranslationsStore()
                    const errorMessage = error.response?.data?.error || t('match_errors.not_a_bot')
                    throw new Error(errorMessage)
                }

                throw error
            }
        },

        async addProfileToMatch(inviteeId) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!inviteeId) {
                throw new Error('Invitee ID is required')
            }

            console.log('Adding profile to match:', { matchId: this.setup.id, inviteeId })

            try {
                const response = await apiService.post(`/api/v1/matches/${this.setup.id}/invite`, {
                    invitee_id: inviteeId,
                })

                this.setup.profiles = response.profiles

                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_INVITE_SENT, {
                    online: !!this.setup.online,
                    player_count: this.setup.profiles?.length ?? 0,
                })

                return response
            } catch (error) {
                console.error('Failed to remove profile from match:', error)

                if (error.response?.status === 404) {
                    const { t } = useTranslationsStore()
                    const errorMessage = error.response?.data?.error || t('match_errors.not_in_match')
                    throw new Error(errorMessage)
                }

                throw error
            }
        },

        async removeProfileFromMatch(profileId) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!profileId) {
                throw new Error('Profile ID is required')
            }

            console.log('Removing profile from match:', { matchId: this.setup.id, profileId })

            try {
                const response = await apiService.post(`/api/v1/matches/${this.setup.id}/leave`, {
                    profile_id: profileId,
                })

                this.setup.profiles = response.profiles

                // Covers both kicking a joined player and cancelling a pending invite: the
                // backend has a single /leave endpoint for both.
                analytics.trackEvent(ANALYTICS_EVENTS.LOBBY_PLAYER_REMOVED, {
                    was_bot: this.isBotProfile(profileId),
                    player_count: this.setup.profiles?.length ?? 0,
                })

                return response
            } catch (error) {
                console.error('Failed to remove profile from match:', error)

                if (error.response?.status === 404) {
                    const { t } = useTranslationsStore()
                    const errorMessage = error.response?.data?.error || t('match_errors.not_in_match')
                    throw new Error(errorMessage)
                }

                throw error
            }
        },

        async updatePlayerTeam(profileId, teamUUID) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!profileId) {
                throw new Error('Profile ID is required')
            }

            console.log('Updating player team:', { matchId: this.setup.id, profileId, teamUUID })

            const profiles = this.setup.profiles || []
            const profileIndex = profiles.findIndex((p) => p.id === profileId)

            if (profileIndex === -1) {
                throw new Error(useTranslationsStore().t('match_errors.player_not_found'))
            }

            const originalTeam = profiles[profileIndex].team
            profiles[profileIndex].team = teamUUID

            try {
                await this.updateMatch()

                console.log(`Updated ${profiles[profileIndex].tag_name} team to:`, teamUUID)

                return { profiles }
            } catch (error) {
                profiles[profileIndex].team = originalTeam
                console.error('Failed to update player team on backend:', error)
                throw error
            }
        },

        async updatePlayersTeams(teamUpdates) {
            if (!this.setup.id) {
                throw new Error(useTranslationsStore().t('match_errors.no_match_created'))
            }

            if (!teamUpdates || !Array.isArray(teamUpdates)) {
                throw new Error('Team updates array is required')
            }

            console.log('Batch updating player teams:', {
                matchId: this.setup.id,
                updates: teamUpdates,
            })

            const profiles = this.setup.profiles || []
            const originalTeams = new Map()

            for (const { profileId, teamUUID } of teamUpdates) {
                const profileIndex = profiles.findIndex((p) => p.id === profileId)

                if (profileIndex === -1) {
                    throw new Error(useTranslationsStore().t('match_errors.player_not_found'))
                }

                originalTeams.set(profileId, profiles[profileIndex].team)

                profiles[profileIndex].team = teamUUID
                console.log(`Updated ${profiles[profileIndex].tag_name} team to:`, teamUUID)
            }

            try {
                await this.updateMatch()

                console.log('Batch team update completed successfully')
                return { profiles }
            } catch (error) {
                for (const { profileId } of teamUpdates) {
                    const profileIndex = profiles.findIndex((p) => p.id === profileId)
                    if (profileIndex !== -1) {
                        profiles[profileIndex].team = originalTeams.get(profileId)
                    }
                }

                console.error('Failed to batch update player teams on backend:', error)
                throw error
            }
        },

        async restoreMatchFromAPI(matchId) {
            console.log('Restoring match from API with ID:', matchId, 'type:', typeof matchId)

            if (!matchId) {
                console.log('No match ID provided for restoration')
                return
            }

            try {
                const matchData = await apiService.get(`/api/v1/matches/${matchId}`)

                if (!matchData || !matchData.match.id) {
                    console.log('No match data returned from API')
                    return
                }

                console.log('Match data fetched from API:', matchData)

                if (!matchData.rt_matchdata) {
                    this.setup = {
                        id: matchData.match.id,
                        matchtype_id: matchData.match.matchtype_id,
                        online: matchData.match.online || false,
                        min_players: matchData.match.min_players || 2,
                        max_players: matchData.match.max_players || 10,
                        name: matchData.match.name || 'Game',
                        matchmaking: matchData.match.matchmaking || false,
                        profiles: matchData.match.profiles || [],
                        settings: matchData.match.settings || {
                            win_type: 'first_to',
                            check_in: 'single',
                            check_out: 'double',
                            legs_to_win: 1,
                            sets_to_win: 1,
                            starting_score: 501,
                            darts_per_turn: 3,
                        },
                        dart_setup_id: matchData.match.dart_setup_id || null,
                        starting_player: matchData.match.starting_player || null,
                        include_in_stats: matchData.match.include_in_stats ?? true,
                        creator_profile_id: matchData.match.creator_profile_id || null,
                    }
                }

                if (matchData.match.rt_matchdata) {
                    this.cancelRtFlush()
                    const rtMatchData =
                        matchData.match.rt_matchdata &&
                        typeof matchData.match.rt_matchdata === 'object'
                            ? matchData.match.rt_matchdata
                            : {}
                    this.match = {
                        id: matchData.match.id,
                        matchtype_id: matchData.match.matchtype_id,
                        online: matchData.match.online || false,
                        matchData: {
                            ...rtMatchData,
                            match_detail_data: rtMatchData.match_detail_data ?? {},
                        },
                        gatekeeper: matchData.match.rt_gatekeeper || [],
                        creator_profile_id: matchData.match.creator_profile_id || null,
                    }
                }

                this.joinMatchChannel(matchData.match.id)

                console.log('Match restored successfully from API')
                return matchData
            } catch (error) {
                console.error('Failed to restore match from API:', error)
                this._captureError(error, {
                    tags: { action: 'restoreMatchFromAPI' },
                    extra: { matchId },
                })

                if (error.response?.status === 404 || error.response?.status === 403) {
                    this.clearMatch()
                }

                throw error
            }
        },

        calculateDartScore(dart) {
            if (Object.hasOwn(DART_TYPE_FIXED_VALUES, dart.dart_type)) {
                return DART_TYPE_FIXED_VALUES[dart.dart_type]
            }

            const multiplier = DART_TYPE_MULTIPLIERS[dart.dart_type] || 1
            return dart.dart_number * multiplier
        },

        parseDartValue(dartScore) {
            if (!dartScore) return 0

            if (dartScore.startsWith('D') || dartScore.startsWith('T')) {
                return parseInt(dartScore.substring(1)) || 0
            }
            if (dartScore === 'Bull') return 50
            if (dartScore === 'Outer Bull') return 25
            if (dartScore === '0') return 0

            return parseInt(dartScore) || 0
        },

        async submitDart(dartData) {
            if (!this.match.id) {
                throw new Error('No active match to submit dart to')
            }

            try {
                const payload = {
                    action: 'submit_dart',
                    match_id: this.match.id,
                    profile_id: dartData.profile_id,
                    dart: {
                        dart_dt: new Date().toISOString(),
                        dart_number: dartData.dart_number,
                        dart_type: dartData.dart_type,
                        dart_x: 0,
                        dart_y: 0,
                        dart_index: dartData.dart_index,
                    },
                    halo_macaddress: dartData.halo_macaddress || null,
                }

                console.log('Submitting dart data:', payload)

                const response = await apiService.post('/api/v1/dart-data', payload)

                console.log('Dart submitted successfully:', response)

                if (response?.rt_matchdata || response?.rt_gatekeeper) {
                    this.applyAuthoritativeRtMatchSnapshot(response)
                }

                return response
            } catch (error) {
                console.error('Failed to submit dart:', error)
                this._captureError(error, {
                    tags: { action: 'submitDart' },
                    extra: {
                        matchId: this.match.id,
                        profileId: dartData.profile_id,
                        dartData: {
                            dart_number: dartData.dart_number,
                            dart_type: dartData.dart_type,
                            dart_index: dartData.dart_index,
                        },
                    },
                })
                throw error
            }
        },

        async updateDart(dartData) {
            if (!this.match.id && !this.setup.id) {
                throw new Error('No active match to update dart in')
            }

            const matchId = this.match.id || this.setup.id

            try {
                const payload = {
                    action: 'update_dart',
                    match_id: matchId,
                    profile_id: dartData.profile_id,
                    set_index: this.editingTurn.set_index,
                    leg_index: this.editingTurn.leg_index,
                    turn_index: this.editingTurn.turn_index,
                    dartboard_uuid: dartData.dartboard_uuid || null,
                    dart: {
                        dart_dt: dartData.dart_dt || new Date().toISOString(),
                        dart_number: dartData.dart_number,
                        dart_type: dartData.dart_type,
                        dart_x: dartData.dart_x || 0,
                        dart_y: dartData.dart_y || 0,
                        dart_index: dartData.dart_index,
                    },
                    halo_macaddress: dartData.halo_macaddress || null,
                }

                const response = await apiService.post('/api/v1/dart-data', payload)

                if (response?.rt_matchdata || response?.rt_gatekeeper) {
                    this.applyAuthoritativeRtMatchSnapshot(response)
                }

                return response
            } catch (error) {
                console.error('Failed to update dart:', error)
                this._captureError(error, {
                    tags: { action: 'updateDart' },
                    extra: {
                        matchId,
                        editingTurn: this.editingTurn,
                        dartData: {
                            dart_number: dartData.dart_number,
                            dart_type: dartData.dart_type,
                        },
                    },
                })
                throw error
            }
        },

        async updateTurnData(turnData) {
            if (!this.match.id) {
                throw new Error('No active match to update turn data for')
            }

            const dart = {}
            if (turnData.double_attempts !== undefined && turnData.double_attempts !== null) {
                dart.double_attempts = turnData.double_attempts
            }
            if (turnData.darts_thrown !== undefined && turnData.darts_thrown !== null) {
                dart.darts_thrown = turnData.darts_thrown
            }

            const payload = {
                action: 'update_turn_data',
                match_id: this.match.id,
                profile_id: turnData.profile_id,
                dart,
                halo_macaddress: null,
            }

            const response = await apiService.post('/api/v1/dart-data', payload)

            if (response?.rt_matchdata || response?.rt_gatekeeper) {
                this.applyAuthoritativeRtMatchSnapshot(response)
            }

            return response
        },

        async processChoice(choice, profileId) {
            if (!this.match.id && !this.setup.id) {
                throw new Error('No active match to process choice for')
            }

            const matchId = this.match.id || this.setup.id

            try {
                const payload = {
                    action: 'process_choice',
                    match_id: matchId,
                    profile_id: profileId,
                    choice: choice,
                }

                console.log('Processing choice:', payload)

                const response = await apiService.post('/api/v1/dart-data', payload)

                console.log('Choice processed successfully:', response)

                if (response?.rt_matchdata || response?.rt_gatekeeper) {
                    this.applyAuthoritativeRtMatchSnapshot(response)
                }

                return response
            } catch (error) {
                console.error('Failed to process choice:', error)
                throw error
            }
        },

        async submitTurn(dartScores, profileId) {
            if (!this.match.id) {
                throw new Error('No active match to submit turn to')
            }

            if (!dartScores || dartScores.length === 0) {
                throw new Error('No dart scores provided')
            }

            try {
                const results = []

                for (let i = 0; i < dartScores.length; i++) {
                    const dartScore = dartScores[i]
                    const dartIndex = i + 1

                    const result = await this.submitDart({
                        profile_id: profileId,
                        dart_score: dartScore,
                        dart_index: dartIndex,
                    })
                    results.push(result)
                }

                console.log('Turn submitted successfully:', results)
                return results
            } catch (error) {
                console.error('Failed to submit turn:', error)
                throw error
            }
        },

        clearMatch() {
            console.log('Clearing match data')
            this.clearRtQueueAndCancelFlush()

            this.setup = {
                id: null,
                matchtype_id: null,
                online: false,
                min_players: 2,
                max_players: 10,
                name: 'Game',
                matchmaking: false,
                profiles: [],
                settings: {
                    win_type: 'first_to',
                    check_in: 'single',
                    check_out: 'double',
                    legs_to_win: 1,
                    sets_to_win: 1,
                    starting_score: 501,
                    darts_per_turn: 3,
                },
                dart_setup_id: null,
                starting_player: null,
                currentStep: 1,
                totalSteps: 4,
                include_in_stats: true,
            }
            this.match = {
                id: null,
                matchtype_id: null,
                online: false,
                matchData: {
                    sets: [],
                    profiles: [],
                    settings: {},
                },
            }
            this.playMode = 'singles'
            this.showValidationError = false
            this.team1UUID = null
            this.team2UUID = null

            echoService.setAllowedMatchChannelValidator(null)

            setTimeout(() => {
                this.leaveMatchChannel()
            }, 0)

            console.log('Match data cleared')
        },

        setCurrentStep(step) {
            this.setup.currentStep = step
        },

        getStepInfo() {
            const steps = {
                1: { name: 'Game Type', route: 'games' },
                2: { name: 'Settings', route: 'matchtype-settings-selector' },
                3: { name: 'Players', route: 'player-selector' },
                4: { name: 'Setup', route: 'setup-selector' },
            }

            return {
                current: this.setup.currentStep,
                total: this.setup.totalSteps,
                currentStepName: steps[this.setup.currentStep]?.name || 'Unknown',
                steps,
            }
        },

        setPlayMode(mode) {
            if (mode !== 'singles' && mode !== 'teams') {
                throw new Error('Invalid play mode. Must be "singles" or "teams"')
            }
            this.playMode = mode
        },

        setShowValidationError(show) {
            this.showValidationError = show
        },

        setTeamUUIDs(team1UUID, team2UUID) {
            this.team1UUID = team1UUID
            this.team2UUID = team2UUID
        },

        clearTeamUUIDs() {
            this.team1UUID = null
            this.team2UUID = null
        },

        startWebsocketHealthMonitor() {
            if (this.websocketHealth.connectionHandlersBound) return

            if (!echoService.instance?.connector?.pusher?.connection) {
                console.warn('Cannot monitor websocket health: Echo connection not available')
                return
            }

            const connection = echoService.instance.connector.pusher.connection

            connection.bind('disconnected', this.handleWebsocketDisconnected.bind(this))
            connection.bind('connected', this.handleWebsocketConnected.bind(this))
            connection.bind('unavailable', this.handleWebsocketDisconnected.bind(this))
            connection.bind('failed', this.handleWebsocketDisconnected.bind(this))

            this.websocketHealth.connectionHandlersBound = true

            if (!echoService.connected) {
                this.handleWebsocketDisconnected()
            }

            console.log('Websocket health monitor started (connection state based)')
        },

        stopWebsocketHealthMonitor() {
            if (
                this.websocketHealth.connectionHandlersBound &&
                echoService.instance?.connector?.pusher?.connection
            ) {
                const connection = echoService.instance.connector.pusher.connection
                connection.unbind('disconnected', this.handleWebsocketDisconnected)
                connection.unbind('connected', this.handleWebsocketConnected)
                connection.unbind('unavailable', this.handleWebsocketDisconnected)
                connection.unbind('failed', this.handleWebsocketDisconnected)
            }

            this.stopPollingFallback()

            this.websocketHealth.isDisconnected = false
            this.websocketHealth.connectionHandlersBound = false

            console.log('Websocket health monitor stopped')
        },

        handleWebsocketDisconnected() {
            if (this.websocketHealth.isDisconnected) return

            console.warn('Websocket disconnected, starting polling fallback')
            this.websocketHealth.isDisconnected = true
            this.startPollingFallback()
        },

        handleWebsocketConnected() {
            if (!this.websocketHealth.isDisconnected) return

            console.log('Websocket reconnected, stopping polling fallback')
            this.websocketHealth.isDisconnected = false
            this.stopPollingFallback()

            // Pusher/Reverb never replay events broadcast while a socket was
            // dropped, so a brief blip (e.g. a mobile network handoff) can
            // silently swallow the other player's turn-change event. The
            // reconnect itself is the only signal we get, so use it to pull
            // one authoritative snapshot and catch up on whatever was missed.
            this.pollMatchData()
        },

        startPollingFallback() {
            if (this.websocketHealth.pollingInterval) return

            this.websocketHealth.pollingInterval = setInterval(() => {
                this.pollMatchData()
            }, 5000)

            this.pollMatchData()

            console.log('Polling fallback started (5s interval)')
        },

        stopPollingFallback() {
            if (this.websocketHealth.pollingInterval) {
                clearInterval(this.websocketHealth.pollingInterval)
                this.websocketHealth.pollingInterval = null
                console.log('Polling fallback stopped')
            }
        },

        async pollMatchData() {
            const matchId = this.match.id || this.setup.id
            if (!matchId) return

            try {
                const matchData = await apiService.get(`/api/v1/matches/${matchId}`)

                if (matchData?.match?.rt_matchdata || matchData?.match?.rt_gatekeeper) {
                    this.applyAuthoritativeRtMatchSnapshot({
                        rt_matchdata: matchData.match.rt_matchdata,
                        rt_gatekeeper: matchData.match.rt_gatekeeper,
                    })
                }

                console.log('Polled match data successfully')
            } catch (error) {
                console.error('Failed to poll match data:', error)
                this._captureError(error, {
                    tags: { action: 'pollMatchData', errorType: 'polling' },
                    extra: { matchId },
                })
            }
        },
    },
})
